<!-- frontend/src/views/PasajeroView.vue -->
<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8 px-4 sm:px-6 lg:px-8">
    <div class="max-w-5xl mx-auto">

      <div v-if="isDemo" class="bg-gradient-to-br from-slate-800 to-slate-900 text-white rounded-lg p-4 mb-4 shadow-lg">
        <div class="flex items-start gap-3">
          <div class="flex-shrink-0">
            <div class="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
              <svg class="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
              </svg>
            </div>
          </div>
          <div class="flex-1">
            <h3 class="font-semibold text-sm">Modo Simulación</h3>
            <p class="text-xs text-gray-300 mt-1">Visualizando datos locales. Para ver información real, usa un código diferente.</p>
          </div>
        </div>
      </div>
      
      <!-- Header con navegación y selector de idioma -->
      <div class="flex items-center justify-between mb-8">
        <button
          @click="$router.push('/')"
          class="group flex items-center gap-2 text-gray-300 hover:text-cyan-400 transition-all duration-200 font-medium"
        >
          <span class="text-xl group-hover:-translate-x-1 transition-transform">←</span>
          <span>{{ t('back') }}</span>
        </button>
        
        <!-- Selector de idioma -->
        <div class="flex items-center gap-2 bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-1">
          <button
            v-for="lang in languages"
            :key="lang.code"
            @click="setLanguage(lang.code)"
            :class="[
              'px-3 py-1.5 rounded-lg text-sm font-medium transition-all',
              currentLang === lang.code
                ? 'bg-gradient-to-r from-cyan-400 to-lime-400 text-gray-900 font-bold shadow-md'
                : 'text-gray-300 hover:bg-gray-700'
            ]"
          >
            {{ lang.flag }} {{ lang.code.toUpperCase() }}
          </button>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 text-center border border-gray-700">
        <div class="inline-flex items-center justify-center w-20 h-20 mb-6 bg-gradient-to-br from-cyan-400 to-lime-400 rounded-full">
          <div class="animate-spin rounded-full h-10 w-10 border-4 border-gray-900 border-t-transparent"></div>
        </div>
        <h3 class="text-xl font-bold text-white mb-2">{{ t('loadingTitle') }}</h3>
        <p class="text-gray-400">{{ t('loadingSubtitle') }}</p>
        <div class="mt-8 flex justify-center gap-1">
          <div class="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style="animation-delay: 0ms"></div>
          <div class="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style="animation-delay: 150ms"></div>
          <div class="w-2 h-2 bg-cyan-400 rounded-full animate-bounce" style="animation-delay: 300ms"></div>
        </div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="bg-gray-800 rounded-3xl shadow-2xl p-8 md:p-12 border border-gray-700">
        <div class="flex items-start gap-4 mb-6">
          <div class="flex-shrink-0 w-14 h-14 bg-red-900/30 rounded-2xl flex items-center justify-center border border-red-500/30">
            <span class="text-2xl">⚠️</span>
          </div>
          <div>
            <h3 class="text-xl font-bold text-white mb-1">{{ t('errorTitle') }}</h3>
            <p class="text-gray-400">{{ errorMessage }}</p>
          </div>
        </div>
        
        <div class="flex flex-col sm:flex-row gap-3 mt-8">
          <button
            @click="retry"
            :disabled="retrying"
            class="flex-1 px-6 py-3.5 bg-gradient-to-r from-cyan-500 to-lime-500 hover:from-cyan-400 hover:to-lime-400 disabled:from-gray-600 disabled:to-gray-600 text-gray-900 font-bold rounded-xl transition-all flex items-center justify-center gap-2 shadow-lg hover:shadow-cyan-500/25"
          >
            <span v-if="retrying" class="animate-spin">⟳</span>
            <span>{{ retrying ? t('retrying') : t('retry') }}</span>
          </button>
          <button
            @click="$router.push('/')"
            class="flex-1 px-6 py-3.5 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-xl transition-all border border-gray-600"
          >
            {{ t('backToHome') }}
          </button>
        </div>
      </div>

      <!-- Success State - Reserva completa -->
      <div v-else-if="reservation" class="space-y-6">
        
        <!-- Tarjeta principal: Datos del pasajero -->
        <div class="bg-gray-800 rounded-3xl shadow-2xl overflow-hidden border border-gray-700">

          <!-- Header responsive optimizado -->
          <div class="bg-gradient-to-r from-cyan-700 via-cyan-600 to-cyan-700 px-4 sm:px-6 py-4 sm:py-5">
            <div class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              
              <!-- Sección izquierda: Logo + Pasajero -->
              <div class="flex items-center gap-4">
                <!-- Logo TS con borde animado -->
                <div class="relative flex-shrink-0">
                  <div class="absolute inset-0 bg-gradient-to-br from-cyan-400 to-lime-400 rounded-full blur opacity-50 animate-pulse"></div>
                  <div class="relative w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-gray-900 flex items-center justify-center border-2 border-cyan-400/50">
                    <span class="text-xl sm:text-2xl font-bold bg-gradient-to-br from-cyan-400 to-lime-400 bg-clip-text text-transparent">TS</span>
                  </div>
                </div>
                
                <!-- Divider vertical (solo desktop) -->
                <div class="hidden sm:block w-px h-12 bg-gradient-to-b from-transparent via-cyan-400 to-transparent"></div>
                
                <!-- Info pasajero -->
                <div class="flex-1 min-w-0">
                  <h1 class="text-lg sm:text-xl font-bold text-white mb-0.5 truncate">{{ passengerName }}</h1>
                  <p class="text-xs sm:text-sm text-cyan-200 truncate">{{ passengerEmail }}</p>
                </div>
              </div>
              
              <!-- Sección derecha: Booking ID (destacado en móvil) -->
              <div class="w-full sm:w-auto">
                <div class="bg-gray-900/30 backdrop-blur-sm rounded-lg px-4 py-2 sm:px-5 sm:py-2.5 border border-cyan-400/30">
                  <div class="flex sm:block items-center justify-between sm:text-right gap-3">
                    <div>
                      <p class="text-cyan-200 text-xs uppercase tracking-wider mb-0.5 sm:mb-1">Booking ID</p>
                      <p class="text-base sm:text-lg font-bold text-white font-mono">#{{ reservationCode }}</p>
                    </div>
                    <!-- Icono de ticket (solo móvil) -->
                    <div class="flex-shrink-0 text-cyan-300">
                      <svg class="w-6 h-6 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"></path>
                      </svg>
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
          
          <!-- Cuerpo: Información del pasajero -->
          <div class="p-6 md:p-8">
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">

              <!-- OC Reference -->
              <div class="bg-gray-700/50 rounded-2xl p-4 border border-gray-600">
                <p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">🔖 {{ t('purchaseOrder') }}</p>
                <p class="text-white font-bold">{{ datapax.oc }}</p>
              </div>

              <!-- Fecha reserva -->
              <div class="bg-gray-700/50 rounded-2xl p-4 border border-gray-600">
                <p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">📅 {{ t('reservationDate') }}</p>
                <p class="text-white font-semibold">{{ formatDate(datapax.fecha_reserva) }}</p>
              </div>

              <!-- Teléfono -->
              <div class="bg-gray-700/50 rounded-2xl p-4 border border-gray-600">
                <p class="text-xs font-medium text-gray-400 uppercase tracking-wide mb-1">📱 {{ t('phone') }}</p>
                <p class="text-white font-semibold">{{ datapax.telefono }}</p>
              </div>

              <!-- Total -->
              <div class="bg-gradient-to-br from-cyan-900/30 to-lime-900/30 rounded-2xl p-4 border border-cyan-500/30">
                <p class="text-xs font-medium text-cyan-300 uppercase tracking-wide mb-1">💰 {{ t('totalReservation') }}</p>
                <p class="text-white font-bold text-lg">${{ formatCurrency(datapax.total_reserva) }}</p>
              </div>
              

            </div>

            <!-- Divider -->
            <div class="border-t border-gray-700 my-6"></div>

            <!-- Lista de servicios -->
            <h3 class="text-lg font-bold text-white mb-4 flex items-center gap-2">
              <span>🗓️</span>
              <span>{{ t('yourServices') }}</span>
              <span class="px-2.5 py-1 bg-gradient-to-r from-cyan-700 to-cyan-700 text-white rounded-lg text-xs font-bold">
                {{ datatour.length }}
              </span>
            </h3>

            <div class="space-y-4">
              <div 
                v-for="(service, index) in datatour" 
                :key="service.bp || index"
                class="group bg-gray-700/30 rounded-2xl p-5 border-2 hover:shadow-xl transition-all duration-300"
                :class="getServiceBorderClass(service.cierre)"
              >
                <div class="flex flex-col xl:flex-row xl:items-start justify-between gap-4">
                  <!-- Info principal del servicio -->
                  <div class="flex-1 min-w-0">
                    <div class="flex items-start justify-between gap-3 mb-3">
                      <div>
                        <h4 class="font-bold text-white text-lg leading-tight">{{ service.servicio }}</h4>
                        <p class="text-sm text-gray-400 mt-1">{{ t('boardingPass') }}: <span class="font-mono text-cyan-400">{{ service.bp }}</span></p>
                      </div>
                      <span :class="getCierreBadgeClass(service.cierre)" class="px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap shadow-sm">
                        {{ formatCierreStatus(service.cierre) }}
                      </span>
                    </div>
                    
                    <!-- Detalles en grid -->
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm mb-4">
                      <div class="flex items-center gap-2 text-gray-300">
                        <span class="text-cyan-400">📅</span>
                        <span><strong>{{ t('date') }}:</strong> {{ formatDate(service.fecha_servicio) }}</span>
                      </div>
                      <div class="flex items-center gap-2 text-gray-300">
                        <span class="text-cyan-400">📍</span>
                        <span><strong>{{ t('meetingPoint') }}:</strong> {{ service.encuentro }}</span>
                      </div>
                      <div class="flex items-center gap-2 text-gray-300">
                        <span class="text-cyan-400">⏰</span>
                        <span><strong>{{ t('pickupTime') }}:</strong> {{ formatPickupTime(service.hora_pickup) }}</span>
                      </div>
                      <div class="flex items-center gap-2 text-gray-300">
                        <span class="text-cyan-400">👥</span>
                        <span><strong>{{ t('passengers') }}:</strong> {{ service.pasajero }}</span>
                      </div>
                    </div>

                    <!-- Desglose financiero -->
                    <div class="bg-gray-800 rounded-xl p-4 border border-gray-600 mt-4">
                      <p class="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">💵 {{ t('paymentBreakdown') }}</p>
                      <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <!-- Total del servicio -->
                        <div class="bg-gray-700/50 rounded-lg p-3 text-center border border-gray-600">
                          <p class="text-xs text-gray-400 mb-1">{{ t('serviceTotal') }}</p>
                          <p class="text-lg font-bold text-white">${{ formatCurrency(service.total) }}</p>
                        </div>
                        
                        <!-- Monto abonado -->
                        <div class="bg-green-900/30 rounded-lg p-3 text-center border border-green-500/30">
                          <p class="text-xs text-green-400 mb-1">{{ t('amountPaid') }}</p>
                          <p class="text-lg font-bold text-green-300">${{ formatCurrency(service.abono) }}</p>
                        </div>
                        
                        <!-- Total a pagar -->
                        <div class="bg-gray-700/50 rounded-lg p-3 text-center border-2" :class="getPendingBorderClass(service)">
                          <p class="text-xs text-gray-400 mb-1">{{ t('totalToPay') }}</p>
                          <p class="text-xl font-bold text-white">${{ formatCurrency(getAmountPending(service)) }}</p>
                          <p v-if="getAmountPending(service) === 0" class="text-xs text-lime-400 font-semibold mt-1">
                            ✅ {{ t('paid') }}
                          </p>
                        </div>
                      </div>
                      
                      <!-- Barra de progreso de pago -->
                      <div class="mt-3">
                        <div class="flex justify-between text-xs mb-1">
                          <span class="text-gray-400">{{ t('paymentProgress') }}</span>
                          <span class="font-semibold" :class="getProgressTextClass(service)">
                            {{ getPaymentProgress(service) }}%
                          </span>
                        </div>
                        <div class="w-full bg-gray-700 rounded-full h-2.5 overflow-hidden">
                          <div 
                            class="h-full rounded-full transition-all duration-500 bg-gradient-to-r from-cyan-400 to-lime-400"
                            :style="{ width: getPaymentProgress(service) + '%' }"
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
            <!-- NUEVO: Políticas, Checkbox y Descarga de Boarding Pass -->
            <div class="mt-8 pt-6 border-t border-gray-700 space-y-5">
              
              <!-- Enlace a Políticas y Condiciones -->
              <div class="flex justify-center">
                <button
                  v-if="reservation.politicas_url != ''" 
                  @click="goToPolicies"
                  class="group flex items-center gap-2 text-cyan-400 hover:text-lime-400 transition-colors duration-200 font-medium text-sm"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                  </svg>
                  <span>{{ t('policiesLink') }}</span>
                </button>
              </div>

              <!-- Checkbox de aceptación -->
              <div class="flex justify-center"  v-if="reservation.politicas_url != ''" >
                <label class="flex items-center gap-3 cursor-pointer group select-none max-w-lg">
                  <!-- Checkbox personalizado -->
                  <div class="relative flex-shrink-0">
                    <input 
                      type="checkbox" 
                      v-model="aceptaPoliticas"
                      class="peer sr-only"
                    />
                    <div class="w-6 h-6 rounded-lg border-2 border-gray-500 bg-gray-700 peer-checked:bg-gradient-to-br peer-checked:from-cyan-400 peer-checked:to-lime-400 peer-checked:border-cyan-400 transition-all duration-200 flex items-center justify-center group-hover:border-cyan-400">
                      <svg 
                        class="w-4 h-4 text-gray-900 opacity-0 peer-checked:opacity-100 transition-opacity duration-200"
                        :class="{ 'opacity-100': aceptaPoliticas }"
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"></path>
                      </svg>
                    </div>
                  </div>
                  <!-- Texto del checkbox -->
                  <span class="text-sm text-gray-300 group-hover:text-white transition-colors leading-snug">
                    {{ t('acceptPolicies') }}
                  </span>
                </label>
              </div>

              <!-- Botón Descargar Boarding Pass -->
              <div class="flex justify-center">
                <button
                  :disabled="!aceptaPoliticas || isDownloading"
                  @click="downloadBoardingPass"
                  v-if="reservation.politicas_url != ''" 
                  :class="[
                    'group flex items-center gap-3 px-8 py-4 rounded-2xl font-bold text-base transition-all duration-300 shadow-lg',
                    aceptaPoliticas && !isDownloading
                      ? 'bg-gradient-to-r from-cyan-500 to-lime-500 hover:from-cyan-400 hover:to-lime-400 text-gray-900 hover:shadow-cyan-500/30 hover:shadow-xl hover:-translate-y-0.5 cursor-pointer'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed border border-gray-600'
                  ]"
                >
                  <span v-if="isDownloading" class="animate-spin text-lg">⟳</span>
                  <svg 
                    v-else
                    class="w-5 h-5 transition-transform group-hover:translate-y-0.5" 
                    :class="aceptaPoliticas ? 'text-gray-900' : 'text-gray-500'"
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                  </svg>
                  <span>{{ isDownloading ? t('generatingPdf') : t('downloadBoardingPass') }}</span>
                </button>
              </div>

            </div>
          </div>
          
          <!-- Footer de la tarjeta -->
          <div class="px-6 py-4 bg-gray-700/50 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div class="flex items-center gap-2 text-sm text-gray-400">
              <span>🕐</span>
              <span>{{ t('updated') }}: {{ formattedTimestamp }}</span>
            </div>
            <div class="flex items-center gap-2">
              <button
                @click="shareReservation"
                class="px-4 py-2 text-sm text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-1.5"
              >
                <span>🔗</span>
                <span>{{ t('share') }}</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Acciones adicionales -->
        <div class="flex flex-wrap gap-3">
          <button
            @click="retry"
            class="px-5 py-2.5 bg-gray-800 border border-gray-600 hover:bg-gray-700 text-white font-medium rounded-xl transition-all flex items-center gap-2 shadow-lg hover:shadow-xl"
          >
            <span>🔄</span>
            <span>{{ t('refresh') }}</span>
          </button>

        </div>

      </div>

    </div>

    <!-- Contenedor siempre presente, pero oculto visualmente hasta que se imprime -->
    <div 
      v-show="showPdfContainer" 
      id="pdf-render-container" 
      style="position: fixed; top: 0; left: 0; width: 600px; z-index: -9999; opacity: 0.01; pointer-events: none; background: #ffffff; padding: 20px;"
    >

      <BoardingPassCard
        v-for="(service, idx) in datatour"
        :key="'bp-' + idx"
        :service="service"
        :reservation-code="reservationCode"
        :passenger-name="passengerName"
        :passenger-email="passengerEmail"
        :passenger-phone="datapax.telefono || ''"
      />

    </div>
  </div>
</template>

<script>
import apiService from '@/services/apiConfig';
import BoardingPassCard from '@/components/BoardingPassCard.vue';

export default {
  name: 'PasajeroView',

  components: {
    BoardingPassCard
  },
  
  props: {
    encodedParam: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      aceptaPoliticas: false, 
      isDownloading: false,
      showPdfContainer: false,
      isDemo: false,
      loading: false,
      retrying: false,
      error: null,
      errorMessage: '',
      reservation: null,
      timestamp: null,
      sourceUrl: null,
      copiedCode: null,
      currentLang: localStorage.getItem('lang') || 'es',
      languages: [
        { code: 'es', flag: '🇪🇸', name: 'Español' },
        { code: 'en', flag: '🇬🇧', name: 'English' },
        { code: 'pt', flag: '🇧🇷', name: 'Português' }
      ],
      translations: {
        es: {
          back: 'Volver',
          loadingTitle: 'Cargando tu reserva',
          loadingSubtitle: 'Consultando información de tu viaje...',
          errorTitle: 'No pudimos cargar tu reserva',
          retry: 'Reintentar',
          retrying: 'Reintentando...',
          backToHome: 'Volver al inicio',
          reservation: 'Reserva',
          service: 'servicio',
          services: 'servicios',
          purchaseOrder: 'ORDEN DE COMPRA',
          reservationDate: 'Fecha reserva',
          phone: 'Teléfono',
          totalReservation: 'Total reserva',
          yourServices: 'Tus servicios',
          boardingPass: 'Boarding Pass',
          date: 'Fecha',
          meetingPoint: 'Punto encuentro',
          pickupTime: 'Hora pickup',
          passengers: 'Pasajeros',
          paymentBreakdown: 'Desglose de pago',
          serviceTotal: 'Total servicio',
          amountPaid: 'Monto abonado',
          totalToPay: 'Total a pagar',
          paid: 'Pagado',
          paymentProgress: 'Progreso de pago',
          updated: 'Actualizado',
          print: 'Imprimir',
          share: 'Compartir',
          refresh: 'Actualizar',
          statusPending: 'Pendiente',
          statusPaid: 'Pagado',
          statusCancelled: 'Anulado',
          statusRefunded: 'Reembolsado',
          error400: 'El enlace de reserva no es válido o está mal formado.',
          error401: 'No tienes autorización para acceder a esta reserva.',
          error403: 'Acceso denegado. Verifica tu enlace.',
          error404: 'No encontramos una reserva con esos datos.',
          error429: 'Demasiados intentos. Espera unos minutos.',
          error500: 'Error temporal del servidor. Intenta más tarde.',
          error502: 'No pudimos conectar con el sistema de reservas.',
          error503: 'Servicio temporalmente no disponible.',
          error504: 'La solicitud tardó demasiado. Reintenta.',
          policiesLink: 'Políticas y Condiciones',
          acceptPolicies: 'He leído y estoy de acuerdo con las políticas y condiciones',
          downloadBoardingPass: 'Descargar Boarding Pass',
          downloading: 'Descargando...',
          generatingPdf: 'Generando PDF...',
        },
        en: {
          back: 'Back',
          loadingTitle: 'Loading your reservation',
          loadingSubtitle: 'Consulting your trip information...',
          errorTitle: 'We could not load your reservation',
          retry: 'Retry',
          retrying: 'Retrying...',
          backToHome: 'Back to home',
          reservation: 'Reservation',
          service: 'service',
          services: 'services',
          purchaseOrder: 'PURCHASE ORDER',
          reservationDate: 'Reservation date',
          phone: 'Phone',
          totalReservation: 'Total reservation',
          yourServices: 'Your services',
          boardingPass: 'Boarding Pass',
          date: 'Date',
          meetingPoint: 'Meeting point',
          pickupTime: 'Pickup time',
          passengers: 'Passengers',
          paymentBreakdown: 'Payment breakdown',
          serviceTotal: 'Service total',
          amountPaid: 'Amount paid',
          totalToPay: 'Total to pay',
          paid: 'Paid',
          paymentProgress: 'Payment progress',
          updated: 'Updated',
          print: 'Print',
          share: 'Share',
          refresh: 'Refresh',
          statusPending: 'Pending',
          statusPaid: 'Paid',
          statusCancelled: 'Cancelled',
          statusRefunded: 'Refunded',
          error400: 'The reservation link is invalid or malformed.',
          error401: 'You are not authorized to access this reservation.',
          error403: 'Access denied. Check your link.',
          error404: 'We could not find a reservation with that data.',
          error429: 'Too many attempts. Wait a few minutes.',
          error500: 'Temporary server error. Try again later.',
          error502: 'We could not connect to the reservation system.',
          error503: 'Service temporarily unavailable.',
          error504: 'The request took too long. Retry.',
          policiesLink: 'Policies and Conditions',
          acceptPolicies: 'I have read and agree to the policies and conditions',
          downloadBoardingPass: 'Download Boarding Pass',
          downloading: 'Downloading...',
          generatingPdf: 'Generating PDF...',
        },
        pt: {
          back: 'Voltar',
          loadingTitle: 'Carregando sua reserva',
          loadingSubtitle: 'Consultando informações da sua viagem...',
          errorTitle: 'Não foi possível carregar sua reserva',
          retry: 'Tentar novamente',
          retrying: 'Tentando novamente...',
          backToHome: 'Voltar ao início',
          reservation: 'Reserva',
          service: 'serviço',
          services: 'serviços',
          purchaseOrder: 'ORDEM DE COMPRA',
          reservationDate: 'Data da reserva',
          phone: 'Telefone',
          totalReservation: 'Total da reserva',
          yourServices: 'Seus serviços',
          boardingPass: 'Boarding Pass',
          date: 'Data',
          meetingPoint: 'Ponto de encontro',
          pickupTime: 'Horário de retirada',
          passengers: 'Passageiros',
          paymentBreakdown: 'Detalhamento do pagamento',
          serviceTotal: 'Total do serviço',
          amountPaid: 'Valor pago',
          totalToPay: 'Total a pagar',
          paid: 'Pago',
          paymentProgress: 'Progresso do pagamento',
          updated: 'Atualizado',
          print: 'Imprimir',
          share: 'Compartilhar',
          refresh: 'Atualizar',
          statusPending: 'Pendente',
          statusPaid: 'Pago',
          statusCancelled: 'Cancelado',
          statusRefunded: 'Reembolsado',
          error400: 'O link da reserva é inválido ou está malformado.',
          error401: 'Você não está autorizado a acessar esta reserva.',
          error403: 'Acesso negado. Verifique seu link.',
          error404: 'Não encontramos uma reserva com esses dados.',
          error429: 'Muitas tentativas. Aguarde alguns minutos.',
          error500: 'Erro temporário do servidor. Tente mais tarde.',
          error502: 'Não foi possível conectar ao sistema de reservas.',
          error503: 'Serviço temporariamente indisponível.',
          error504: 'A solicitação demorou muito. Tente novamente.',
          policiesLink: 'Políticas e Condições',
          acceptPolicies: 'Li e concordo com as políticas e condições',
          downloadBoardingPass: 'Baixar Boarding Pass',
          downloading: 'Baixando...',
          generatingPdf: 'Gerando PDF...',
        }
      },
    };
  },
  
  computed: {
    datapax() {
      return this.reservation?.datapax || {};
    },
    
    datatour() {
      return this.reservation?.datatour || [];
    },
    
    passengerName() {
      return this.datapax.nombre?.trim().replace(/\s+/g, ' ') || 'Pasajero';
    },
    
    passengerEmail() {
      return this.datapax.email?.trim().replace(/\s+/g, ' ') || '';
    },

    reservationCode() {
      return this.datapax.rl || 'N/A';
    },
    
    servicesCount() {
      return this.datapax.num_servicios || this.datatour.length;
    },
    
    formattedTimestamp() {
      if (!this.timestamp) return '';
      const locale = this.currentLang === 'es' ? 'es-CL' : (this.currentLang === 'pt' ? 'pt-BR' : 'en-US');
      return new Date(this.timestamp).toLocaleString(locale, {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    }
  },
  
  created() {
    console.log('🔍 PasajeroView loaded with param:', this.encodedParam);
    this.fetchReservation();
  },

  methods: {
    t(key) {
      return this.translations[this.currentLang][key] || key;
    },
    
    setLanguage(lang) {
      this.currentLang = lang;
      localStorage.setItem('lang', lang);
    },
    
    async fetchReservation() {
      this.loading = true;
      this.error = null;
      this.errorMessage = '';
      this.retrying = false;
      this.isDemo = false;
      
      try {
        const response = await apiService.getPasajeroEncoded(this.encodedParam);
        
        if (response.ok) {
          console.log("Environment: ", response.environment);
          this.reservation = response.data;
          this.timestamp = response.timestamp;
          this.sourceUrl = response.source;
          this.isDemo = response.demo === true;  // ← Detectar modo demo
        } else {
          //console.log("Aqui", response.data);
          this.handleError(response.error, response.status);
        }
        
      } catch (err) {
        console.error('❌ Error fetching reservation:', err);
        this.handleError(
          err.message || 'Error de conexión con el servidor',
          err.status || 500
        );
      } finally {
        this.loading = false;
      }
    },
    
    handleError(message, status) {
      this.error = true;
      
      const errorKeys = {
        400: 'error400',
        401: 'error401',
        403: 'error403',
        404: 'error404',
        429: 'error429',
        500: 'error500',
        502: 'error502',
        503: 'error503',
        504: 'error504'
      };
      
      this.errorMessage = this.t(errorKeys[status]) || message || this.t('error500');
    },
    
    async retry() {
      this.retrying = true;
      await this.fetchReservation();
      this.retrying = false;
    },
    
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const date = new Date(dateStr + 'T00:00:00');
      const locale = this.currentLang === 'es' ? 'es-CL' : (this.currentLang === 'pt' ? 'pt-BR' : 'en-US');
      return date.toLocaleDateString(locale, {
        weekday: 'short', day: '2-digit', month: 'short', year: 'numeric'
      }).replace(/\./g, '');
    },
    
    formatCurrency(amount) {
      if (amount === null || amount === undefined) return '0';
      const locale = this.currentLang === 'es' ? 'es-CL' : (this.currentLang === 'pt' ? 'pt-BR' : 'en-US');
      return new Intl.NumberFormat(locale).format(amount);
    },
    
    formatPickupTime(time) {
      const cleanTime = time?.replace(':00', '') || time;
      return cleanTime;
    },
    
    formatCierreStatus(cierre) {
      const statusMap = {
        1: 'statusPending',
        2: 'statusPaid',
        3: 'statusCancelled',
        5: 'statusRefunded'
      };
      return this.t(statusMap[cierre]) || `${cierre}`;
    },
    
    getCierreBadgeClass(cierre) {
      const classes = {
        1: 'bg-amber-900/30 text-amber-300 border border-amber-500/30',
        2: 'bg-green-900/30 text-green-300 border border-green-500/30',
        3: 'bg-red-900/30 text-red-300 border border-red-500/30',
        5: 'bg-purple-900/30 text-purple-300 border border-purple-500/30'
      };
      return classes[cierre] || 'bg-gray-700 text-gray-300 border border-gray-600';
    },
    
    getServiceBorderClass(cierre) {
      const classes = {
        1: 'border-amber-500/30 hover:border-amber-400',
        2: 'border-green-500/30 hover:border-green-400',
        3: 'border-red-500/30 hover:border-red-400',
        5: 'border-purple-500/30 hover:border-purple-400'
      };
      return classes[cierre] || 'border-gray-600 hover:border-gray-500';
    },
    
    getAmountPending(service) {
      if(service.cierre == 2){
        return 0;
      }
      const total = parseFloat(service.total) || 0;
      const abono = parseFloat(service.abono) || 0;
      return total - abono;
    },
    
    getPaymentProgress(service) {
      if(service.cierre == 2){
        return 100;
      }
      const total = parseFloat(service.total) || 0;
      const abono = parseFloat(service.abono) || 0;
      if (total === 0) return 0;
      return Math.min(100, Math.round((abono / total) * 100));
    },
    
    getProgressTextClass(service) {
      const progress = this.getPaymentProgress(service);
      if (progress === 100) return 'text-green-400';
      if (progress >= 50) return 'text-cyan-400';
      if (progress > 0) return 'text-amber-400';
      return 'text-red-400';
    },
    
    getPendingBorderClass(service) {
      const pending = this.getAmountPending(service);
      if (pending === 0) return 'border-green-500/30 bg-green-900/20';
      return 'border-gray-600 bg-gray-700/50';
    },
    
    async shareReservation() {
      const shareUrl = window.location.href;
      try {
        if (navigator.share) {
          await navigator.share({
            title: `${this.t('reservation')} #${this.reservationCode}`,
            text: 'TourPass',
            url: shareUrl
          });
        } else {
          await navigator.clipboard.writeText(shareUrl);
          alert('✅ ' + (this.currentLang === 'es' ? 'Enlace copiado' : this.currentLang === 'pt' ? 'Link copiado' : 'Link copied'));
        }
      } catch (err) {
        console.error('❌ Error sharing:', err);
      }
    },
    goToPolicies() {
      const finalUrl = this.reservation.politicas_url;
      if (finalUrl) {
        // Abre el PDF directamente en una nueva pestaña. Fin del problema.
        window.open(finalUrl, '_blank', 'noopener,noreferrer');
      }
    },
    async downloadBoardingPass() {
      if (!this.aceptaPoliticas) return;
      
      this.isDownloading = true;
      
      try {
        // 1. Mostrar el contenedor original para que Vue lo renderice y genere los QR
        this.showPdfContainer = true;
        await this.$nextTick();
        
        // 2. Pausa obligatoria: dar tiempo al componente hijo para generar el código QR en base64
        await new Promise(resolve => setTimeout(resolve, 800));
        
        // 3. Obtener el contenedor ya renderizado
        const original = document.getElementById('pdf-render-container');
        if (!original) throw new Error('Contenedor de impresión no encontrado');
        
        // 4. Clonar el nodo (true = clonar también todos los hijos)
        const clone = original.cloneNode(true);
        clone.id = 'print-clone'; // ID único que el CSS de impresión buscará
        
        // 5. Aplicar estilos inline de seguridad al clon para garantizar su posición
        Object.assign(clone.style, {
          position: 'fixed',
          top: '0',
          left: '0',
          width: '148mm',
          background: '#ffffff',
          zIndex: '999999',
          padding: '10mm',
          display: 'block',
          visibility: 'visible'
        });
        
        // 6. Ajustar las tarjetas individuales dentro del clon para controlar los saltos de página
        const cards = clone.querySelectorAll('.boarding-pass-card');
        cards.forEach((card, index) => {
          card.style.width = '128mm'; // 148mm (ancho total) - 20mm (padding del contenedor)
          card.style.marginBottom = index === cards.length - 1 ? '0' : '10mm';
          card.style.pageBreakAfter = index === cards.length - 1 ? 'avoid' : 'always';
          card.style.breakAfter = index === cards.length - 1 ? 'avoid' : 'page';
          card.style.display = 'block';
        });
        
        // 7. Insertar el clon directamente en el body (fuera de #app o cualquier contenedor padre)
        document.body.appendChild(clone);
        
        // 8. Pequeña pausa para que el navegador aplique los estilos globales al clon
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // 9. Ejecutar la impresión nativa del navegador
        window.print();
        
        // 10. Limpieza: eliminar el clon y ocultar el contenedor original
        document.body.removeChild(clone);
        this.showPdfContainer = false;
        
      } catch (err) {
        console.error('❌ Error al preparar la descarga:', err);
        alert('Ocurrió un error al generar la vista previa. Intenta nuevamente.');
      } finally {
        this.isDownloading = false;
      }
    }
  }
};
</script>

<style scoped>
button {
  @apply transition-all duration-200;
}

button:active:not(:disabled) {
  @apply transform scale-[0.98];
}

.group:hover {
  @apply transform -translate-y-0.5;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
}
.animate-bounce {
  animation: bounce 1.4s ease-in-out infinite;
}
</style>